/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { Identifier, PlayerMediaTrack } from "../../shared/types";

import { AdTracker, PlayerDimension } from "./Advertisement";
import { CastSender } from "./ChromecastSender";
import { PlayerErrorMessage } from "./error";
import { PlayerEvent } from "./events";

export type FairPlayKeySystem = {
  certificateUri: string;
  licenseUri: string;
  options: {
    persistentState: "required";
  };
};

export type PlayerSource = {
  id?: Identifier;
  src: string;
  type?: string;
  adsTagUrl?: string;
  headers?: { [key: string]: string };
  subtitles?: PlayerMediaTrack[];
  // EME options
  emeHeaders?: { [key: string]: unknown };
  // DRM options
  keySystems?: {
    "com.widevine.alpha"?: string;
    "com.microsoft.playready"?: string;
    "com.apple.fps.1_0"?: FairPlayKeySystem;
  };
};

export type PlayerTimer = {
  currentTime: number;
  duration: number;
  liveDuration: number;
  isLive: boolean;
  atLiveEdge: boolean;
  isInfinityStream: boolean;
  progress: number;
};

type PlayerBaseTrack = {
  id: string;
  label: string;
  active: boolean;
};

export type PlayerVideoTrack = PlayerBaseTrack & {
  id: string | "auto";
  width?: number;
  height?: number;
  bitrate?: number;
  framerate?: number;
};

export type PlayerAudioTrack = PlayerBaseTrack & {
  language: string;
};

export type PlayerSubtitleTrack = PlayerBaseTrack & {
  id: string | "disabled";
  language: string;
};

export type ControllerOptions = {
  videoElement: HTMLVideoElement;
  imaContainer: HTMLDivElement;
  onReady: () => void;
  onReload: () => void;
  withClientSideAds: boolean;
  isAppleDevice: boolean;
  sessionToken?: string;
};

export enum AirPlayAvailability {
  AVAILABLE = "available",
  NOT_AVAILABLE = "not-available",
}

export enum AirPlayEvent {
  AVAILABILITY_CHANGED = "webkitplaybacktargetavailabilitychanged",
  TARGET_CHANGED = "webkitcurrentplaybacktargetiswirelesschanged",
}

export interface AirPlayAvailabilityChangeEvent extends Event {
  availability: AirPlayAvailability;
}

type BasePlayerController = {
  initialize: () => void;

  loadMedia: (
    source: PlayerSource,
    autoplay?: boolean,
    startPosition?: number,
  ) => void;

  onEvent: (
    event: PlayerEvent | PlayerEvent[],
    callback: (event?: unknown) => void,
  ) => () => void;

  onVideoTrackChange: (callback: () => void) => () => void;

  onAudioTrackChange: (callback: () => void) => () => void;

  onSubtitleTrackChange: (callback: () => void) => () => void;

  videoTracks: () => PlayerVideoTrack[];

  audioTracks: () => PlayerAudioTrack[];

  subtitleTracks: () => PlayerSubtitleTrack[];

  setVideoTrack: (id: string | "auto") => void;

  setAudioTrack: (id: string) => void;

  setSubtitleTrack: (id: string | "disable") => void;

  requestNativeFullScreen: () => Promise<void>;

  exitNativeFullScreen: () => Promise<void>;

  toggleNativeFullScreen: () => Promise<void>;

  isNativeFullScreen: () => boolean;

  onEnterFullScreen: () => void;

  onLeaveFullScreen: () => void;

  error: () => PlayerErrorMessage | null;

  setError: (message: string, messageKey?: string) => void;

  reload: () => void;

  dispose: () => void;
};

type VodPlayerController = {
  play: () => void;

  resume: (time: number) => void;

  pause: () => void;

  isPaused: () => boolean;

  currentTime: (position?: number) => number;

  duration: () => number;

  seekForward: (seconds: number) => void;

  seekBackward: (seconds: number) => void;

  playbackRate: (value?: number) => number;

  volume: (value?: number) => number;

  muted: (state?: boolean) => boolean;
};

type LivePlayerController = {
  isLive: () => boolean;

  liveSeekableStart: () => number;

  liveSeekableEnd: () => number;

  liveCurrentTime: () => number;

  liveWindow: () => number;

  atLiveEdge: () => boolean;

  seekToLiveEdge: () => void;
};

type AdvertisementPlayerController = {
  pauseAd: () => void;

  resumeAd: () => void;

  skipAd: () => void;

  adCuePoints: () => number[];

  currentAdTracker: () => AdTracker | undefined;
};

type ChromecastSenderController = {
  castSender?: CastSender;

  isCastConnected: () => boolean;

  getCastDeviceName: () => string;
};

type AirPlayController = {
  toggleAirPlay: () => void;

  onAirPlayAvailabilityChange: (
    callback: (availability: AirPlayAvailability) => void,
  ) => () => void;

  onAirPlayStatusChange: (callback: (isCasting: boolean) => void) => () => void;
};

export type PlayerController = BasePlayerController &
  VodPlayerController &
  LivePlayerController &
  AdvertisementPlayerController &
  ChromecastSenderController &
  AirPlayController;

export type CurrentMediaState = {
  source: PlayerSource;
  autoplay?: boolean;
  startPosition?: number;
};

export type ClientSideAdvertisement = {
  initialize(tagUrl: string, startMuted: boolean): void;
  dispose(): void;
  onPlayerError(): void;
  onPlayerReload(): void;
  readonly tracker: AdTracker;
  readonly cuePoints: number[];
  volume(value: number): void;
  muted(state: boolean): void;
  pause(): void;
  resume(): void;
  skip(): void;
  onEnterFullScreen(): void;
  onLeaveFullScreen(): void;
};

export type AdsEventManager = {
  volume(value: number): void;
  muted(state: boolean): void;
  pause(): void;
  resume(): void;
  skip(): void;
  initialize(dimension: PlayerDimension, onInit: () => void): void;
  dispose(): void;
  onPlayerResize(width: number, height: number): void;
  onEnterFullScreen(): void;
  onLeaveFullScreen(width: number, height: number): void;
  contentComplete(): void;
};

export interface PlayerReferenceBase {
  imaContainerElement: HTMLDivElement;
  videoElement: HTMLVideoElement;
}

export type PlayerTracker = {
  initialize: () => void;
  getPlayerWidth(): number;
  getPlayerHeight(): number;
  reset(): void;
  dispose(): void;
};
